import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ReadingSettings, ReadingSettingsPanel } from './ReadingSettings';
import { useReadingPrefs } from './readingPrefs';
import { useTranslation } from 'react-i18next';
import { Clock, Eye, EyeOff, Loader2, Maximize2, Minimize2, Sigma } from 'lucide-react';
import { Button } from 'components/ui/button';
import { ConfirmDialog } from 'components/ui/confirm-dialog';
import { cn } from 'lib/utils';
import { useExamSession } from '../../hooks/useExamSession';
import { useFullscreen } from '../../hooks/useFullscreen';
import { formatClock, isAnswered, isSatKind, KIND_LABEL_KEY } from '../../constants';
import type { ExamConfig } from '../../types';
import { IeltsSectionView } from './IeltsSectionView';
import { IeltsTopBar } from './IeltsTopBar';
import { SectionReviewDialog } from './SectionReviewDialog';
import { SessionBanners } from './SessionBanners';
import { SectionEndScreen, type SectionEndStats } from './SectionEndScreen';
import { buildSectionReview } from './sectionReview';
import { ExamFlagsProvider, useExamFlags } from './ExamFlags';
import { flagUpdates } from './flagMeta';
import { clearFlags } from './flagStore';
import { SatModuleView } from './SatModuleView';
import { ReferenceSheet } from './ReferenceSheet';
import { clearHighlights } from './highlightStore';
import { clearSatModuleState } from './satModuleState';
import { clearSectionPart } from './sectionPart';

/** Tool buttons on the navy IELTS bar. */
const DARK_TOOL = 'text-slate-300 hover:bg-white/10 hover:text-white focus-visible:outline-[#D1FE17] [@media(pointer:coarse)]:!h-11 [@media(pointer:coarse)]:!w-11';

interface Props {
  attemptId: string;
  testTitle: string;
  examConfig: ExamConfig;
  /** Shown in the top bar under the test title. */
  candidateName?: string;
  initialAnswers: Map<string, unknown>;
  /** Called once when the last section closes (by the candidate or the clock). */
  onFinished: (status: string) => void;
}

/**
 * Full-screen runner for IELTS-style and Digital SAT practice attempts.
 * Sections are timed and locked by the server; the runner shows the open one.
 */
export function StandardizedExamRunner(props: Props) {
  return (
    <ExamFlagsProvider attemptId={props.attemptId}>
      <RunnerBody {...props} />
    </ExamFlagsProvider>
  );
}

function RunnerBody({ attemptId, testTitle, examConfig, candidateName, initialAnswers, onFinished }: Props) {
  const { t } = useTranslation();
  const { flagged } = useExamFlags();
  // A request from the review grid to open one question; the section id keeps it from replaying in the next section.
  const [jump, setJump] = useState<{ sectionId: string; questionId: string; seq: number } | null>(null);
  const reading = useReadingPrefs();
  const session = useExamSession({
    attemptId,
    initialAnswers,
    onFinished: (status) => {
      clearHighlights(attemptId);
      clearSatModuleState(attemptId);
      clearSectionPart(attemptId);
      clearFlags(attemptId);
      onFinished(status);
    },
  });
  const { examState, section, remaining, onBreak } = session;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [timerHidden, setTimerHidden] = useState(false);
  const [referenceOpen, setReferenceOpen] = useState(false);
  // Coming back to an attempt that was already under way (answers saved, or a section closed): say so once.
  const [resumedTitle, setResumedTitle] = useState<string | null>(null);
  const resumeChecked = useRef(false);
  // What the candidate just closed, kept for the screen between sections (a reload there shows the title only).
  const [closed, setClosed] = useState<{ fromId: string; title: string; stats: SectionEndStats } | null>(null);
  const sat = examConfig.standard === 'SAT';
  // The marks set in IELTS go to the server with the answers (SAT keeps and reports its own in the module view).
  const { setMeta } = session;
  const reportedFlags = useRef(flagged);
  useEffect(() => {
    const before = reportedFlags.current;
    reportedFlags.current = flagged;
    if (sat || !section) return;
    for (const { questionId, flagged: marked } of flagUpdates(section, before, flagged)) setMeta(questionId, { flagged: marked });
  }, [flagged, sat, section, setMeta]);
  const tools = examConfig.sat?.tools;
  // Full screen is offered on IELTS Reading and on every Digital SAT module. It
  // is left when the candidate moves to a section without it, or the runner closes.
  const fullscreenAllowed = sat || section?.kind === 'IELTS_READING';
  const fullscreen = useFullscreen();
  const { exit: exitFullscreen } = fullscreen;
  useEffect(() => (fullscreenAllowed ? exitFullscreen : undefined), [fullscreenAllowed, exitFullscreen]);
  // Full screen that ended without the candidate asking (Esc, an alt-tab on some systems): offer the way back.
  const [fullscreenLost, setFullscreenLost] = useState(false);
  const wasFullscreen = useRef(false);
  const choseToLeave = useRef(false);
  useEffect(() => {
    if (!fullscreenAllowed) {
      wasFullscreen.current = false;
      setFullscreenLost(false);
    } else if (fullscreen.active) {
      wasFullscreen.current = true;
      setFullscreenLost(false);
    } else if (wasFullscreen.current) {
      wasFullscreen.current = false;
      if (!choseToLeave.current) setFullscreenLost(true);
      choseToLeave.current = false;
    }
  }, [fullscreenAllowed, fullscreen.active]);
  const toggleFullscreen = () => {
    if (fullscreen.active) choseToLeave.current = true;
    fullscreen.toggle();
  };

  useEffect(() => {
    if (resumeChecked.current || !examState || !section) return;
    resumeChecked.current = true;
    const underway = initialAnswers.size > 0 || examState.sections.some((x) => x.status === 'DONE');
    if (underway) setResumedTitle(examState.sections.find((x) => x.id === section.id)?.title ?? section.title);
  }, [examState, section, initialAnswers]);

  const position = useMemo(() => {
    if (!examState) return { index: 0, total: 0 };
    const index = examState.sections.findIndex((s) => s.id === examState.activeSectionId);
    return { index, total: examState.sections.length };
  }, [examState]);
  const isLast = position.total > 0 && position.index === position.total - 1;
  // Name the section the button leads to: a bare "Next section" left candidates
  // unsure whether it meant the next part of this section or a new section.
  const sectionName = (index: number) => {
    const meta = examState?.sections[index];
    return meta ? meta.title?.trim() || t(KIND_LABEL_KEY[meta.kind]) : '';
  };
  const nextName = isLast ? '' : sectionName(position.index + 1);
  const advanceLabel = isLast ? t('exam.finish') : nextName ? t('exam.go_to_section', { name: nextName }) : t('exam.next_section');
  const confirmTitle = isLast
    ? t('exam.confirm.finish_title')
    : nextName
      ? t('exam.confirm.go_to_title', { current: sectionName(position.index), next: nextName })
      : t('exam.confirm.next_title');
  const unanswered = section ? section.questions.filter((q) => !isAnswered(session.answers.get(q.id))).length : 0;
  const lowTime = remaining !== null && remaining <= 300;
  const review = useMemo(() => (section && !sat ? buildSectionReview(section, session.answers) : null), [section, sat, session.answers]);

  useEffect(() => {
    if (closed && section && section.id !== closed.fromId) setClosed(null);
  }, [closed, section]);

  const jumpTo = (questionId: string) => {
    if (!section) return;
    setConfirmOpen(false);
    setJump((prev) => ({ sectionId: section.id, questionId, seq: (prev?.seq ?? 0) + 1 }));
  };

  const confirmAdvance = async () => {
    if (section && review && !sat) {
      const limit = section.timeLimitSeconds;
      setClosed({
        fromId: section.id,
        title: examState?.sections[position.index]?.title ?? '',
        stats: {
          answered: review.answered,
          unanswered: review.unanswered,
          spentSeconds: limit && remaining !== null ? Math.max(0, limit - remaining) : null,
        },
      });
    }
    // IELTS holds the next section for the candidate; the last one just finishes.
    await session.advance(!sat && !isLast ? { hold: true } : undefined);
    setConfirmOpen(false);
  };

  if (session.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white" role="status">
        <Loader2 className="h-6 w-6 animate-spin text-gray-500" aria-hidden />
        <span className="sr-only">{t('loading')}</span>
      </div>
    );
  }

  if (!examState) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white p-6 text-center">
        <p className="text-gray-700">{session.error || t('exam.load_error')}</p>
        <Button onClick={session.refresh}>{t('exam.retry')}</Button>
      </div>
    );
  }

  if (onBreak && !sat) {
    const sections = examState.sections.filter((x) => x.status !== 'SKIPPED');
    const lastDone = [...sections].reverse().find((x) => x.status === 'DONE');
    const upNext = sections.find((x) => x.status === 'LOCKED');
    return (
      <SectionEndScreen
        testTitle={testTitle}
        finished={{ title: closed?.title || lastDone?.title || '', stats: closed?.stats ?? null }}
        timeline={sections.map((x) => ({ id: x.id, title: x.title, done: x.status === 'DONE' }))}
        next={
          upNext
            ? {
                title: upNext.title?.trim() || t(KIND_LABEL_KEY[upNext.kind]),
                minutes: upNext.timeLimitSeconds ? Math.round(upNext.timeLimitSeconds / 60) : null,
                questions: upNext.questionCount || null,
              }
            : null
        }
        remaining={remaining}
        busy={session.busy}
        onStart={session.resumeBreak}
      />
    );
  }

  if (onBreak) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#0A1628] px-6 text-center text-white">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{t('exam.break.eyebrow')}</p>
        <h1 className="mt-3 text-3xl font-bold sm:text-4xl">{t('exam.break.title')}</h1>
        <p className="mt-3 max-w-md text-slate-300">{t('exam.break.body')}</p>
        <p className="mt-10 font-mono text-6xl font-semibold tabular-nums sm:text-7xl" aria-live="off">
          {formatClock(remaining ?? 0)}
        </p>
        <Button size="lg" onClick={session.resumeBreak} disabled={session.busy} className="mt-10 bg-primary-400 text-gray-950 hover:bg-primary-300">
          {t('exam.break.resume')}
        </Button>
        <p className="mt-4 text-xs text-slate-400">{t('exam.break.auto')}</p>
      </div>
    );
  }

  const activeMeta = examState.sections[position.index];

  const readingSettings = (className: string) => (
    <ReadingSettings
      prefs={reading.prefs}
      onTheme={(theme) => reading.update({ theme })}
      onZoomIn={reading.zoomIn}
      onZoomOut={reading.zoomOut}
      className={className}
    />
  );

  const fullscreenButton = (className: string) =>
    fullscreenAllowed && fullscreen.supported ? (
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleFullscreen}
        aria-label={fullscreen.active ? t('exam.exit_fullscreen') : t('exam.fullscreen')}
        title={fullscreen.active ? t('exam.exit_fullscreen') : t('exam.fullscreen')}
        className={cn('h-9 w-9 shrink-0', className)}
      >
        {fullscreen.active ? <Minimize2 className="h-4 w-4" aria-hidden /> : <Maximize2 className="h-4 w-4" aria-hidden />}
      </Button>
    ) : null;

  return (
    <div className="flex h-[100dvh] flex-col bg-white">
      {sat ? (
        <header
          className={cn(
            'flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-4 py-2.5 sm:px-6',
            sat ? 'border-b-2 border-dashed border-gray-300 bg-[#F5F7FB]' : 'border-gray-200 bg-[#0A1628] text-white',
          )}
        >
          <div className="min-w-0 flex-1 basis-full sm:basis-0">
            <p className={cn('truncate text-xs', sat ? 'text-gray-600' : 'text-slate-400')}>{testTitle}</p>
            <h1 className="truncate text-base font-semibold">
              {activeMeta?.title}
              <span className={cn('ml-2 text-sm font-normal', sat ? 'text-gray-500' : 'text-slate-400')}>
                {t('exam.section_of', { n: position.index + 1, total: position.total })}
              </span>
            </h1>
          </div>

          {remaining !== null && (
            <div className="flex items-center gap-2">
              {!timerHidden || lowTime ? (
                <span
                  className={cn(
                    'flex items-center gap-1.5 rounded-md px-2.5 py-1 font-mono text-lg font-semibold tabular-nums',
                    lowTime ? 'bg-red-600 text-white' : sat ? 'text-gray-900' : 'text-white',
                  )}
                  role="timer"
                  aria-label={t('exam.time_left', { time: formatClock(remaining) })}
                >
                  <Clock className="h-4 w-4" aria-hidden /> {formatClock(remaining)}
                </span>
              ) : (
                <span className="text-sm text-gray-600">{t('exam.timer_hidden')}</span>
              )}
              {sat && (
                <Button variant="ghost" size="sm" onClick={() => setTimerHidden((v) => !v)} aria-pressed={timerHidden} className="gap-1 text-gray-700">
                  {timerHidden ? <Eye className="h-4 w-4" aria-hidden /> : <EyeOff className="h-4 w-4" aria-hidden />}
                  {timerHidden ? t('exam.show_timer') : t('exam.hide_timer')}
                </Button>
              )}
            </div>
          )}

          <div className="flex items-center gap-1.5">
            {readingSettings('text-gray-700 hover:bg-gray-200/70 hover:text-gray-900')}
            {fullscreenButton('text-gray-700 hover:bg-gray-200/70 hover:text-gray-900')}
            {sat && section?.kind === 'SAT_MATH' && tools?.referenceSheet !== false && (
              <Button variant="ghost" size="sm" onClick={() => setReferenceOpen(true)} className="gap-1.5">
                <Sigma className="h-4 w-4" aria-hidden /> {t('exam.sat.reference')}
              </Button>
            )}
            {!sat && (
              <Button
                size="sm"
                onClick={() => setConfirmOpen(true)}
                disabled={session.busy}
                className="bg-primary-400 text-gray-950 hover:bg-primary-300"
              >
                {advanceLabel}
              </Button>
            )}
          </div>
          {examState.versionCode && (
            <span className={cn('w-full text-right font-mono text-[11px] sm:w-auto', sat ? 'text-gray-500' : 'text-slate-400')}>
              {t('exam.version_code', { code: examState.versionCode })}
            </span>
          )}
        </header>
      ) : (
        <IeltsTopBar
          testTitle={testTitle}
          sectionTitle={activeMeta?.title ?? ''}
          position={t('exam.section_of', { n: position.index + 1, total: position.total })}
          remaining={remaining}
          lowTime={lowTime}
          timerHidden={timerHidden}
          onToggleTimer={() => setTimerHidden((v) => !v)}
          saving={session.saving}
          versionCode={examState.versionCode}
          candidateName={candidateName}
          tools={
            <>
              {readingSettings(DARK_TOOL)}
              {fullscreenButton(DARK_TOOL)}
            </>
          }
          menuTools={
            <>
              <ReadingSettingsPanel prefs={reading.prefs} onTheme={(theme) => reading.update({ theme })} onZoomIn={reading.zoomIn} onZoomOut={reading.zoomOut} />
              {fullscreenAllowed && fullscreen.supported && (
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  className="mt-4 flex min-h-11 w-full items-center gap-3 rounded-xl border border-[#E6E9F2] px-3.5 text-left text-sm font-semibold text-[#10182B] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B]"
                >
                  {fullscreen.active ? <Minimize2 className="h-5 w-5" aria-hidden /> : <Maximize2 className="h-5 w-5" aria-hidden />}
                  {fullscreen.active ? t('exam.exit_fullscreen') : t('exam.fullscreen')}
                </button>
              )}
            </>
          }
          finishes={isLast}
          actionLabel={isLast ? t('exam.finish') : t('exam.review.open')}
          actionTitle={advanceLabel}
          onAction={() => setConfirmOpen(true)}
          busy={session.busy}
        />
      )}

      <SessionBanners
        fullscreenLost={fullscreenLost}
        onReturnToFullscreen={fullscreen.toggle}
        saveFailed={session.saveFailed}
        onRetrySave={() => void session.retrySave()}
        timeUpTitle={session.timeUpTitle}
        onDismissTimeUp={session.dismissTimeUp}
        resumedTitle={resumedTitle}
        onDismissResumed={() => setResumedTitle(null)}
      />

      {session.saving && <span className="sr-only" role="status">{t('exam.saving')}</span>}

      {section ? (
        <div
          className="exam-reading flex min-h-0 flex-1 flex-col"
          data-reading-theme={reading.prefs.theme}
          // `zoom` scales the passage and questions together, layout included.
          style={{ zoom: reading.prefs.zoom } as React.CSSProperties}
        >
        {isSatKind(section.kind) ? (
          <SatModuleView
            key={section.id}
            attemptId={attemptId}
            section={section}
            answers={session.answers}
            onAnswer={session.setAnswer}
            onMeta={session.setMeta}
            registerSettle={session.registerSettle}
            annotatorEnabled={tools?.annotator !== false}
            eliminatorEnabled={tools?.eliminator !== false}
            onFinishModule={() => setConfirmOpen(true)}
            busy={session.busy}
          />
        ) : (
          <IeltsSectionView
            key={section.id}
            attemptId={attemptId}
            section={section}
            answers={session.answers}
            onAnswer={session.setAnswer}
            onRecorded={session.setLocalAnswer}
            onMeta={session.setMeta}
            registerSettle={session.registerSettle}
            jump={jump && jump.sectionId === section.id ? jump : null}
          />
        )}
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center p-6 text-gray-600">{t('exam.load_error')}</div>
      )}

      <ReferenceSheet open={referenceOpen} onClose={() => setReferenceOpen(false)} />

      {review ? (
        <SectionReviewDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          sectionTitle={activeMeta?.title ?? ''}
          review={review}
          remaining={remaining}
          advanceLabel={advanceLabel}
          busy={session.busy}
          onConfirm={confirmAdvance}
          flagged={flagged}
          onJump={jumpTo}
        />
      ) : (
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={confirmTitle}
        description={
          <>
            {t('exam.confirm.no_return')}
            {unanswered > 0 && <span className="mt-2 block font-medium text-amber-700">{t('exam.confirm.unanswered', { count: unanswered })}</span>}
          </>
        }
        confirmLabel={advanceLabel}
        cancelLabel={t('exam.confirm.stay')}
        loading={session.busy}
        onConfirm={confirmAdvance}
      />
      )}
    </div>
  );
}

export default StandardizedExamRunner;
